import { Container, Graphics, Text } from "pixi.js"
import { CONTINUE_CHOICE, ConversationView } from "game-common/conversation"
import { TextRenderer } from "../text/text_renderer"
import { trackLetters } from "../text/typography"
import { FACE, GOLD, INK, MUTED, PLOT_FACE, PLOT_FACE_ON, PLOT_RIM, RIM, TIMBER, TIMBER_EDGE, chamfer, drawBoard } from "./build_board"

/**
 * A conversation with an NPC (prototype; the server walks the tree, campaigns/atoms/conversations.ts).
 *
 *   - What the NPC says stands over its head on a timber plate with a tail down to it, the speaker's name on
 *     top, typed out a letter at a time. A press on the plate, Enter or Space shows the whole line, then the
 *     next one.
 *   - What the player can answer comes up once the last line is shown, on a board docked at the bottom of
 *     the screen: numbered rows, picked by their number, a click or a tap. Not a bubble, so it never reads as
 *     the player already speaking.
 *   - Leave (Esc on a keyboard) ends it at any time; so does walking off (GuiManager).
 *
 * The rest of the HUD is out of the way while it is open (HudPresence.setConversing).
 */
export interface ConversationPanelCallbacks {
    choose: (view: ConversationView, choiceId: string) => void
    leave: (view: ConversationView) => void
}

export interface ConversationViewport { left: number; top: number; width: number; height: number; right: number; bottom: number }

const BUBBLE_W = 300
const BUBBLE_PAD = 14
const NAME_GAP = 6
const TAIL_H = 12
const TAIL_HALF = 8
const BOARD_MAX_W = 480
const BOARD_PAD = 12
const ROW_H = 26
const ROW_GAP = 4
const KEYCAP = 18
const EDGE = 12
const TYPE_CHARS_PER_MS = 0.045
const FADE_MS = 160

interface Row {
    view: Container
    plate: Graphics
    keycap: Graphics
    key: Text
    label: Text
    choiceId: string
}

export class ConversationPanel extends Container {
    private view: ConversationView | null = null
    private line = 0
    private typed = 0
    private hovered = -1
    private opacity = 0
    private boardAlpha = 0
    private touch = false

    private bubble = new Container()
    private bubbleBg = new Graphics()
    private nameText: Text
    private lineText: Text
    private moreText: Text
    private bubbleSize = { w: 0, h: 0 }
    private tailAt = -1

    private board = new Container()
    private boardBg = new Graphics()
    private rows: Row[] = []
    private boardSize = { w: 0, h: 0 }

    private leaveButton = new Container()
    private leaveBg = new Graphics()
    private leaveText: Text

    constructor(private callbacks: ConversationPanelCallbacks) {
        super()
        this.nameText = new TextRenderer().role("compactCaption").bold().strokeThickness(0).render("") as Text
        this.nameText.tint = GOLD
        this.lineText = new TextRenderer().role("body").fontSize(13).strokeThickness(0).wordWrap(BUBBLE_W - BUBBLE_PAD * 2).render("") as Text
        this.lineText.tint = INK
        this.moreText = new TextRenderer().role("compactCaption").strokeThickness(0).render("") as Text
        this.moreText.tint = MUTED
        this.bubble.addChild(this.bubbleBg, this.nameText, this.lineText, this.moreText)
        this.bubble.interactive = true
        this.bubble.buttonMode = true
        this.bubble.on("pointertap", () => this.advance())

        this.board.addChild(this.boardBg)

        this.leaveText = new TextRenderer().role("compactLabel").bold().strokeThickness(0).render("") as Text
        this.leaveText.tint = INK
        this.leaveButton.addChild(this.leaveBg, this.leaveText)
        this.leaveButton.interactive = true
        this.leaveButton.buttonMode = true
        this.leaveButton.on("pointertap", () => this.view && this.callbacks.leave(this.view))

        this.addChild(this.bubble, this.board, this.leaveButton)
        this.visible = false
    }

    isOpen = (): boolean => !!this.view
    current = (): ConversationView | null => this.view

    /** Show a node. A resend of the node already up changes nothing. */
    show = (view: ConversationView, touch: boolean) => {
        if (this.view?.step === view.step) return
        const sameNpc = this.view?.npcEntityId === view.npcEntityId
        this.view = view
        this.touch = touch
        this.line = 0
        this.typed = 0
        this.hovered = -1
        this.boardAlpha = 0
        if (!sameNpc) this.opacity = 0
        this.nameText.text = trackLetters(view.speaker.toUpperCase(), " ")
        this.leaveText.text = touch ? "Leave" : "Leave  Esc"
        this.buildRows()
        this.layoutLine()
        this.visible = true
    }

    close = () => {
        this.view = null
    }

    /** Whether Enter or Space would move the conversation on (a line still to show, or a node with no answers). */
    wantsContinue = (): boolean => !!this.view && (!this.lineDone() || !this.lastLine() || !this.view.choices.length)

    /** A key the panel uses, pressed. True when it was used. */
    handleKey = (key: string): boolean => {
        if (!this.view) return false
        if (key === "Escape" || key === "Esc") {
            this.callbacks.leave(this.view)
            return true
        }
        if (key === "Enter" || key === " ") {
            if (this.wantsContinue()) this.advance()
            else if (this.hovered >= 0) this.pick(this.hovered)
            return true
        }
        if (/^[1-9]$/.test(key)) {
            if (this.choicesUp()) this.pick(Number(key) - 1)
            return true
        }
        return false
    }

    /** One frame: type, fade, and stand the plate over `anchor` (stage px over the speaker's head, or null). */
    update = (dtMs: number, anchor: { x: number; y: number } | null, viewport: ConversationViewport) => {
        const target = this.view ? 1 : 0
        this.opacity = approach(this.opacity, target, dtMs / FADE_MS)
        this.alpha = this.opacity
        if (!this.view) {
            if (this.opacity <= 0) this.visible = false
            return
        }
        const scale = this.touch ? 1.25 : 1
        this.bubble.scale.set(scale)
        this.board.scale.set(scale)
        this.leaveButton.scale.set(scale)

        // Typing.
        const full = this.view.lines[this.line] ?? ""
        if (this.typed < full.length) {
            this.typed = Math.min(full.length, this.typed + dtMs * TYPE_CHARS_PER_MS)
            this.lineText.text = full.slice(0, Math.floor(this.typed))
        }
        this.moreText.visible = this.lineDone() && this.wantsContinue()
        if (this.moreText.visible) this.moreText.alpha = 0.55 + 0.45 * Math.abs(Math.sin(performance.now() / 420))

        // The plate over the speaker, held on screen.
        const w = this.bubbleSize.w * scale, h = this.bubbleSize.h * scale
        const tip = anchor ?? { x: viewport.left + viewport.width / 2, y: viewport.top + viewport.height * 0.4 }
        const x = clamp(tip.x - w / 2, viewport.left + EDGE, viewport.right - EDGE - w)
        const y = clamp(tip.y - h - TAIL_H * scale, viewport.top + EDGE, viewport.bottom - EDGE - h)
        this.bubble.position.set(Math.round(x), Math.round(y))
        const tailAt = anchor ? Math.round(clamp((tip.x - x) / scale, TAIL_HALF + 8, this.bubbleSize.w - TAIL_HALF - 8)) : -1
        if (tailAt !== this.tailAt) {
            this.tailAt = tailAt
            this.drawBubble()
        }

        // The answers, docked at the bottom once the last line is shown.
        const up = this.choicesUp()
        this.boardAlpha = approach(this.boardAlpha, up ? 1 : 0, dtMs / FADE_MS)
        this.board.visible = this.boardAlpha > 0 && this.rows.length > 0
        this.board.alpha = this.boardAlpha
        this.board.interactiveChildren = up
        const bw = this.boardSize.w * scale, bh = this.boardSize.h * scale
        const bx = Math.round(viewport.left + (viewport.width - bw) / 2)
        const by = Math.round(viewport.bottom - EDGE - bh + (1 - this.boardAlpha) * 8)
        this.board.position.set(bx, by)

        // Leave: over the board's right end while it is up, at the bottom of the screen otherwise.
        const lw = this.leaveButton.width, lh = this.leaveButton.height
        if (this.board.visible) this.leaveButton.position.set(Math.round(bx + bw - lw), Math.round(by - lh - 6))
        else this.leaveButton.position.set(Math.round(viewport.left + (viewport.width - lw) / 2), Math.round(viewport.bottom - EDGE - lh))
    }

    // ---- moving on ----------------------------------------------------------------------------

    private lineDone = () => !!this.view && this.typed >= (this.view.lines[this.line] ?? "").length
    private lastLine = () => !!this.view && this.line >= this.view.lines.length - 1
    private choicesUp = () => !!this.view && this.lastLine() && this.lineDone() && this.view.choices.length > 0

    private advance = () => {
        const view = this.view
        if (!view) return
        if (!this.lineDone()) {
            this.typed = (view.lines[this.line] ?? "").length
            this.lineText.text = view.lines[this.line] ?? ""
            return
        }
        if (!this.lastLine()) {
            this.line++
            this.typed = 0
            this.layoutLine()
            return
        }
        if (!view.choices.length) this.callbacks.choose(view, CONTINUE_CHOICE)
    }

    private pick = (index: number) => {
        const view = this.view
        const row = this.rows[index]
        if (!view || !row || !this.choicesUp()) return
        this.flash(row)
        this.callbacks.choose(view, row.choiceId)
    }

    // ---- drawing --------------------------------------------------------------------------------

    /** Size the plate for the whole line, then show none of it: it types out without the plate growing. */
    private layoutLine = () => {
        const full = this.view?.lines[this.line] ?? ""
        this.lineText.text = full
        const textH = this.lineText.height
        this.lineText.text = ""
        this.nameText.position.set(BUBBLE_PAD, BUBBLE_PAD - 2)
        const lineTop = BUBBLE_PAD + this.nameText.height + NAME_GAP
        this.lineText.position.set(BUBBLE_PAD, lineTop)
        this.moreText.text = this.touch ? "tap ▸" : "Enter ▸"
        const h = lineTop + textH + BUBBLE_PAD + 8
        this.bubbleSize = { w: BUBBLE_W, h: Math.round(h) }
        this.moreText.position.set(BUBBLE_W - BUBBLE_PAD - this.moreText.width, h - BUBBLE_PAD - this.moreText.height + 4)
        this.tailAt = -2
    }

    private drawBubble = () => {
        const { w, h } = this.bubbleSize
        const g = this.bubbleBg
        g.clear()
        drawBoard(g, w, h)
        if (this.tailAt >= 0) {
            g.lineStyle(1.5, TIMBER_EDGE, 0.95)
            g.beginFill(TIMBER, 0.98)
            g.moveTo(this.tailAt - TAIL_HALF, h - 1).lineTo(this.tailAt, h + TAIL_H).lineTo(this.tailAt + TAIL_HALF, h - 1)
            g.endFill()
            g.lineStyle(0)
            g.beginFill(FACE, 0.97)
            g.moveTo(this.tailAt - TAIL_HALF + 3, h - 4).lineTo(this.tailAt, h + TAIL_H - 5).lineTo(this.tailAt + TAIL_HALF - 3, h - 4)
            g.endFill()
        }
    }

    private buildRows = () => {
        for (const row of this.rows) row.view.destroy({ children: true })
        this.rows = []
        const view = this.view
        if (!view) return
        const w = BOARD_MAX_W
        view.choices.forEach((choice, i) => {
            const row = new Container()
            const plate = new Graphics()
            const keycap = new Graphics()
            const key = new TextRenderer().role("compactLabel").bold().strokeThickness(0).render(`${i + 1}`) as Text
            key.tint = INK
            const label = new TextRenderer().role("body").fontSize(13).strokeThickness(0)
                .wordWrap(w - BOARD_PAD * 2 - KEYCAP - 18).render(choice.say) as Text
            label.tint = INK
            row.addChild(plate, keycap, key, label)
            row.interactive = true
            row.buttonMode = true
            row.on("pointerover", () => this.hover(i))
            row.on("pointerout", () => this.hover(-1))
            row.on("pointertap", () => this.pick(i))
            const entry: Row = { view: row, plate, keycap, key, label, choiceId: choice.id }
            this.rows.push(entry)
            this.board.addChild(row)
        })
        let y = BOARD_PAD
        for (const row of this.rows) {
            const h = Math.max(ROW_H, row.label.height + 8)
            row.view.position.set(BOARD_PAD, y)
            row.keycap.clear()
            row.keycap.lineStyle(1, RIM, 0.9).beginFill(PLOT_FACE, 1)
            chamfer(row.keycap, 6, (h - KEYCAP) / 2, KEYCAP, KEYCAP, 3)
            row.keycap.endFill()
            row.key.position.set(6 + (KEYCAP - row.key.width) / 2, (h - row.key.height) / 2)
            row.label.position.set(6 + KEYCAP + 10, (h - row.label.height) / 2)
            ;(row.view as any).rowH = h
            y += h + ROW_GAP
        }
        this.boardSize = { w, h: Math.round(y - ROW_GAP + BOARD_PAD) }
        this.boardBg.clear()
        drawBoard(this.boardBg, this.boardSize.w, this.boardSize.h)
        this.rows.forEach((_, i) => this.drawRow(i))
        this.drawLeave()
    }

    private hover = (index: number) => {
        const before = this.hovered
        this.hovered = index
        if (before >= 0) this.drawRow(before)
        if (index >= 0) this.drawRow(index)
    }

    private drawRow = (index: number, lit = false) => {
        const row = this.rows[index]
        if (!row) return
        const h = (row.view as any).rowH ?? ROW_H
        const on = lit || index === this.hovered
        row.plate.clear()
        row.plate.lineStyle(1, on ? GOLD : PLOT_RIM, on ? 0.9 : 0.6)
        row.plate.beginFill(on ? PLOT_FACE_ON : PLOT_FACE, on ? 0.95 : 0.55)
        chamfer(row.plate, 0, 0, this.boardSize.w - BOARD_PAD * 2 || BOARD_MAX_W - BOARD_PAD * 2, h, 4)
        row.plate.endFill()
        row.label.tint = on ? GOLD : INK
    }

    private flash = (row: Row) => this.drawRow(this.rows.indexOf(row), true)

    private drawLeave = () => {
        const padX = 10, padY = 5
        const g = this.leaveBg
        g.clear()
        g.lineStyle(1, RIM, 0.85).beginFill(TIMBER, 0.95)
        chamfer(g, 0, 0, this.leaveText.width + padX * 2, this.leaveText.height + padY * 2, 4)
        g.endFill()
        this.leaveText.position.set(padX, padY)
    }
}

const approach = (value: number, target: number, step: number) =>
    value < target ? Math.min(target, value + step) : Math.max(target, value - step)

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value))
